import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import withAuth from '../../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import { 
    FiRefreshCcw, 
    FiDownloadCloud, 
    FiChevronDown, 
    FiChevronUp, 
    FiSearch,
    FiFilter,
    FiX,
    FiAlertCircle,
    FiCheckCircle,
    FiTrendingDown,
    FiTrendingUp,
    FiFileText,
    FiFile,
} from "react-icons/fi";
import { TabContext } from '../../components/TabbedLayout';
import { ReportEmbedContext } from '../reportEmbedContext';
import { GET_REORDER_LEVEL_REPORT, GET_ITEM_PURCHASE_HISTORY, BASE_URL } from '../../utlilities/config';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ExportFormatModal = ({ isOpen, onClose, onSelectFormat }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-96 max-w-md transform transition-transform duration-300 ease-out">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex justify-between items-center rounded-t-2xl">
                    <h2 className="text-xl font-semibold text-white flex items-center">
                        <FiDownloadCloud className="mr-2" /> Export Report
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors duration-200"
                        aria-label="Close modal"
                    >
                        <FiX size={20} />
                    </button>
                </div>
                <div className="p-6">
                    <p className="text-sm text-slate-600 mb-4">Choose export format:</p>
                    <div className="flex flex-col gap-3">
                        <button
                            onClick={() => onSelectFormat('excel')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                                <FiFileText className="text-green-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to Excel</p>
                                <p className="text-xs text-slate-500">Download as .xlsx file</p>
                            </div>
                        </button>
                        <button
                            onClick={() => onSelectFormat('pdf')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
                                <FiFile className="text-red-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to PDF</p>
                                <p className="text-xs text-slate-500">Download as .pdf file</p>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Enhanced Spinner Component
const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
        <p className="text-indigo-600 font-medium">Loading...</p>
      </div>
    </div>
);

export const ReorderLevel = () => {
    const tabContext = useContext(TabContext);
    const embedded = useContext(ReportEmbedContext);
    const [loading, setLoading] = useState(false);
    const [reorderData, setReorderData] = useState([]);
    const [expandedItems, setExpandedItems] = useState(new Set());
    const [purchaseHistories, setPurchaseHistories] = useState({});
    const [loadingHistories, setLoadingHistories] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'needs-reorder', 'in-stock'
    const [sortConfig, setSortConfig] = useState({ key: 'urgency', direction: 'desc' });
    const [reportOption, setReportOption] = useState('default'); // 'default' | 'all'
    const [showExportModal, setShowExportModal] = useState(false);
    const token = localStorage.getItem('accessToken');

    const getReorderDisplayValue = useCallback((item) => {
        if (reportOption === 'all') {
            return parseFloat(item.PurchaseQty ?? 0);
        }
        return parseFloat(item.ReorderLevel || 0);
    }, [reportOption]);

    const baseFilteredData = useMemo(() => {
        if (reportOption === 'all') {
            return reorderData;
        }
        return reorderData.filter(item => item.ReorderLevel > 0);
    }, [reorderData, reportOption]);

    // Calculate urgency score (higher = more urgent)
    const calculateUrgency = (item) => {
        const onhand = parseFloat(item.Onhand || 0);
        const reorderLevel = parseFloat(item.ReorderLevel || 0);
        if (onhand === 0) return 1000; // Out of stock - highest priority
        if (reorderLevel > 0 && onhand < reorderLevel) {
            const percentage = (onhand / reorderLevel) * 100;
            return 1000 - percentage; // Lower stock = higher urgency
        }
        return 0; // Above reorder level
    };

    // Calculate if item needs reorder (Onhand < ReorderLevel)
    const needsReorder = (item) => {
        return parseFloat(item.Onhand || 0) < parseFloat(item.ReorderLevel || 0);
    };

    const getStockPercentage = (item) => {
        const onhand = parseFloat(item.Onhand || 0);
        const reorderLevel = parseFloat(item.ReorderLevel || 0);
        if (reorderLevel === 0) return 100;
        return Math.min(100, (onhand / reorderLevel) * 100);
    };

    // Filter and sort data
    const filteredData = useMemo(() => {
        let filtered = [...baseFilteredData];

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(item => 
                (String(item.Item_No || '').toLowerCase().includes(query)) ||
                (String(item.Item_Name || '').toLowerCase().includes(query)) ||
                (String(item.Company || '').toLowerCase().includes(query))
            );
        }

        // Apply status filter
        if (statusFilter === 'needs-reorder') {
            filtered = filtered.filter(needsReorder);
        } else if (statusFilter === 'in-stock') {
            filtered = filtered.filter(item => !needsReorder(item));
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.key) {
                case 'urgency':
                    aValue = calculateUrgency(a);
                    bValue = calculateUrgency(b);
                    break;
                case 'itemNo':
                    aValue = a.Item_No || '';
                    bValue = b.Item_No || '';
                    break;
                case 'itemName':
                    aValue = (a.Item_Name || '').toLowerCase();
                    bValue = (b.Item_Name || '').toLowerCase();
                    break;
                case 'company':
                    aValue = (a.Company || '').toLowerCase();
                    bValue = (b.Company || '').toLowerCase();
                    break;
                case 'onhand':
                    aValue = parseFloat(a.Onhand || 0);
                    bValue = parseFloat(b.Onhand || 0);
                    break;
                case 'reorderLevel':
                    aValue = getReorderDisplayValue(a);
                    bValue = getReorderDisplayValue(b);
                    break;
                default:
                    return 0;
            }

            if (typeof aValue === 'string') {
                return sortConfig.direction === 'asc' 
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            } else {
                return sortConfig.direction === 'asc' 
                    ? aValue - bValue
                    : bValue - aValue;
            }
        });

        return filtered;
    }, [baseFilteredData, searchQuery, statusFilter, sortConfig, getReorderDisplayValue]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const normalizePurchaseHistory = (history = []) =>
        history.map((entry) => ({
            SupplierName: entry.supplierName ?? entry.SupplierName ?? '-',
            packing: entry.packing ?? entry.Packing ?? '-',
            foc: entry.foc ?? entry.FOC ?? 0,
            Price: entry.purchasePrice ?? entry.Price ?? 0,
        }));

    const getMostAffordablePurchase = (history) => {
        if (!history || history.length === 0) return null;
        return history.reduce((min, purchase) => {
            const currentPrice = parseFloat(purchase.Price || 0);
            const minPrice = parseFloat(min.Price || 0);
            return currentPrice < minPrice && currentPrice > 0 ? purchase : min;
        }, history[0]);
    };

    const getBestSupplierFromItem = (item) => {
        if (!item?.PurchaseHistory?.length) {
            return { supplier: '-', price: '' };
        }
        const best = getMostAffordablePurchase(normalizePurchaseHistory(item.PurchaseHistory));
        if (!best) {
            return { supplier: '-', price: '' };
        }
        const price = parseFloat(best.Price || 0);
        return {
            supplier: best.SupplierName || '-',
            price: price > 0 ? price : '',
        };
    };

    const toggleItemExpansion = async (itemNo) => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(itemNo)) {
            newExpanded.delete(itemNo);
        } else {
            newExpanded.add(itemNo);
            if (!purchaseHistories[itemNo]) {
                const item = reorderData.find((row) => row.Item_No === itemNo);
                if (item && Object.prototype.hasOwnProperty.call(item, 'PurchaseHistory')) {
                    setPurchaseHistories((prev) => ({
                        ...prev,
                        [itemNo]: normalizePurchaseHistory(item.PurchaseHistory || []),
                    }));
                } else {
                    await fetchPurchaseHistory(itemNo);
                }
            }
        }
        setExpandedItems(newExpanded);
    };

    const fetchPurchaseHistory = async (itemNo) => {
        if (loadingHistories.has(itemNo)) return;
        
        setLoadingHistories(prev => new Set(prev).add(itemNo));
        try {
            const response = await axios.get(`${BASE_URL}${GET_ITEM_PURCHASE_HISTORY}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    'itemNo': itemNo,
                },
            });
            
            if (response.status === 200 && response.data.isSucess) {
                const history = response.data.data || [];
                setPurchaseHistories(prev => ({
                    ...prev,
                    [itemNo]: normalizePurchaseHistory(history),
                }));
            } else {
                toast.error('Failed to fetch purchase history', { autoClose: 1000 });
            }
        } catch (error) {
            console.error('Failed to fetch purchase history:', error);
            toast.error('Failed to fetch purchase history', { autoClose: 1000 });
        } finally {
            setLoadingHistories(prev => {
                const newSet = new Set(prev);
                newSet.delete(itemNo);
                return newSet;
            });
        }
    };

    const getReorderLevelReport = useCallback(async () => {
        setLoading(true);
        setExpandedItems(new Set());
        setPurchaseHistories({});
        try {
            const response = await axios.get(`${BASE_URL}${GET_REORDER_LEVEL_REPORT}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    pageNo: 1,
                    pageSize: 100000,
                    option: reportOption,
                },
            });
            
            if (response.status === 200 && response.data.isSucess) {
                const data = response.data.data || [];
                setReorderData(data);
            } else {
                toast.error('Failed to fetch reorder level report', { autoClose: 1000 });
            }
        } catch (error) {
            console.error('Failed to fetch reorder level report:', error);
            toast.error('Failed to fetch reorder level report', { autoClose: 1000 });
        } finally {
            setLoading(false);
        }
    }, [token, reportOption]);

    useEffect(() => {
        getReorderLevelReport();
    }, [getReorderLevelReport]);

    const handleReportOptionChange = (e) => {
        setReportOption(e.target.value);
    };

    const getPriorityLabel = (item) => {
        const onhand = parseFloat(item.Onhand || 0);
        const stockPercentage = getStockPercentage(item);
        if (onhand === 0) return 'Out';
        if (needsReorder(item)) return 'Low';
        if (stockPercentage < 50) return 'Medium';
        return 'Good';
    };

    const buildExportRow = (item, index) => {
        const onhand = parseFloat(item.Onhand || 0);
        const reorderDisplay = getReorderDisplayValue(item);
        const stockPercentage = getStockPercentage(item);
        const row = {
            'S.No': index + 1,
            'Item No': item.Item_No ?? '',
            'Item Name': item.Item_Name || '',
            'Company': item.Company || '',
            'On Hand': onhand,
            'Reorder Level': reorderDisplay,
            'Stock Level %': Number(stockPercentage.toFixed(0)),
            'Status': needsReorder(item) ? 'Needs Reorder' : 'In Stock',
            'Priority': getPriorityLabel(item),
        };

        if (reportOption === 'all') {
            row['Sales Qty (30 Days)'] = parseFloat(item.SalesQty30Days ?? 0);
            row['Master Reorder Level'] = parseFloat(item.ReorderLevel || 0);
        }

        const bestSupplier = getBestSupplierFromItem(item);
        row['Best Supplier'] = bestSupplier.supplier;
        row['Best Price'] = bestSupplier.price !== '' ? bestSupplier.price : '';

        return row;
    };

    const getExportFilenameSuffix = () => {
        const dateStr = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
        const optionLabel = reportOption === 'all' ? 'All' : 'Default';
        return `${optionLabel}_${dateStr}`;
    };

    const handleExport = () => {
        if (filteredData.length === 0) {
            toast.error('No data to export', { autoClose: 2000 });
            return;
        }
        setShowExportModal(true);
    };

    const exportToExcel = () => {
        try {
            const excelData = filteredData.map((item, index) => buildExportRow(item, index));
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);

            const colWidths = reportOption === 'all'
                ? [
                    { wch: 6 }, { wch: 10 }, { wch: 30 }, { wch: 18 },
                    { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 16 },
                    { wch: 10 }, { wch: 18 }, { wch: 18 }, { wch: 28 }, { wch: 12 },
                ]
                : [
                    { wch: 6 }, { wch: 10 }, { wch: 30 }, { wch: 18 },
                    { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 16 },
                    { wch: 10 }, { wch: 28 }, { wch: 12 },
                ];
            ws['!cols'] = colWidths;

            XLSX.utils.book_append_sheet(wb, ws, 'Reorder Level');
            const filename = `Reorder_Level_Report_${getExportFilenameSuffix()}.xlsx`;
            XLSX.writeFile(wb, filename);
            toast.success('Excel file exported successfully', { autoClose: 2000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.error('Failed to export to Excel', { autoClose: 2000 });
        }
    };

    const exportToPDF = () => {
        try {
            const doc = new jsPDF('landscape', 'mm', 'a4');
            const optionLabel = reportOption === 'all' ? 'All' : 'Default';

            doc.setFontSize(16);
            doc.text('Reorder Level Report', 14, 15);
            doc.setFontSize(10);
            doc.text(`Option: ${optionLabel}`, 14, 22);
            doc.text(`Exported: ${new Date().toLocaleDateString('en-GB')}`, 14, 28);
            doc.text(`Items: ${filteredData.length}`, 14, 34);

            const headers = reportOption === 'all'
                ? ['S.No', 'Item No', 'Item Name', 'Company', 'On Hand', 'Reorder Level', 'Sales 30D', 'Stock %', 'Status', 'Priority']
                : ['S.No', 'Item No', 'Item Name', 'Company', 'On Hand', 'Reorder Level', 'Stock %', 'Status', 'Priority'];

            const tableData = filteredData.map((item, index) => {
                const onhand = parseFloat(item.Onhand || 0);
                const reorderDisplay = getReorderDisplayValue(item);
                const stockPercentage = getStockPercentage(item);
                const baseRow = [
                    String(index + 1),
                    String(item.Item_No ?? ''),
                    item.Item_Name || '',
                    item.Company || '',
                    onhand.toLocaleString(),
                    reorderDisplay.toLocaleString(undefined, { maximumFractionDigits: 2 }),
                ];

                if (reportOption === 'all') {
                    baseRow.push(
                        parseFloat(item.SalesQty30Days ?? 0).toLocaleString(),
                        `${stockPercentage.toFixed(0)}%`,
                        needsReorder(item) ? 'Needs Reorder' : 'In Stock',
                        getPriorityLabel(item),
                    );
                } else {
                    baseRow.push(
                        `${stockPercentage.toFixed(0)}%`,
                        needsReorder(item) ? 'Needs Reorder' : 'In Stock',
                        getPriorityLabel(item),
                    );
                }

                return baseRow;
            });

            autoTable(doc, {
                head: [headers],
                body: tableData,
                startY: 40,
                styles: { fontSize: 7, cellPadding: 2 },
                headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [249, 250, 251] },
                margin: { top: 40, left: 14, right: 14 },
            });

            const filename = `Reorder_Level_Report_${getExportFilenameSuffix()}.pdf`;
            doc.save(filename);
            toast.success('PDF file exported successfully', { autoClose: 2000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            toast.error('Failed to export to PDF', { autoClose: 2000 });
        }
    };

    const handleExportFormat = (format) => {
        if (format === 'excel') {
            exportToExcel();
        } else if (format === 'pdf') {
            exportToPDF();
        }
    };

    const SortableHeader = ({ columnKey, label, className = "" }) => (
        <th 
            className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 cursor-pointer select-none hover:bg-slate-50 transition ${className}`}
            onClick={() => handleSort(columnKey)}
        >
            <div className="flex items-center gap-2">
                {label}
                {sortConfig.key === columnKey && (
                    <span className="text-indigo-600">
                        {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                )}
            </div>
        </th>
    );

    return (
        <div className="flex h-full bg-background">
            <ToastContainer
                position="top-center"
                autoClose={1000}
                limit={1}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />
            
            {loading && <Spinner />}

            <ExportFormatModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                onSelectFormat={handleExportFormat}
            />

            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 sm:px-4 mb-2">
                    <div className={`flex flex-col gap-3 sm:gap-4 ${embedded ? 'lg:flex-row lg:justify-end' : 'lg:flex-row lg:items-center lg:justify-between'}`}>
                        {!embedded && (
                            <div className="min-w-0">
                                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                                    Stock Reports
                                </p>
                                <h1 className="flex flex-wrap items-center gap-2 text-lg font-bold text-slate-800">
                                    Reorder Level Report
                                    {filteredData.filter(needsReorder).length > 0 && (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700">
                                            <FiAlertCircle size={12} />
                                            {filteredData.filter(needsReorder).length} Urgent
                                        </span>
                                    )}
                                </h1>
                            </div>
                        )}
                        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
                            <select
                                value={reportOption}
                                onChange={handleReportOptionChange}
                                className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-black shadow-sm transition hover:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="default">Default</option>
                                <option value="all">All</option>
                            </select>
                            <button
                                type="button"
                                onClick={getReorderLevelReport}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-black shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                <FiRefreshCcw size={16} />
                                Refresh
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-black shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                <FiDownloadCloud size={16} />
                                Export
                            </button>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-hidden flex flex-col">
                    <div className="flex flex-col gap-4 flex-1 min-h-0">

                        {/* Filters and Search Section */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex-shrink-0">
                            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                {/* Search */}
                                <div className="relative flex-1 max-w-md">
                                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                                    <input
                                        type="text"
                                        placeholder="Search by Name, or Company..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                                    />
                                    {searchQuery && (
                                        <button
                                            onClick={() => setSearchQuery('')}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            <FiX size={18} />
                                        </button>
                                    )}
                                </div>

                                {/* Status Filter */}
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-slate-600">Filter:</span>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setStatusFilter('all')}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                                                statusFilter === 'all'
                                                    ? 'bg-indigo-600 text-white'
                                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                            }`}
                                        >
                                            All ({baseFilteredData.length})
                                        </button>
                                        <button
                                            onClick={() => setStatusFilter('needs-reorder')}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                                                statusFilter === 'needs-reorder'
                                                    ? 'bg-red-600 text-white'
                                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                            }`}
                                        >
                                            Urgent ({baseFilteredData.filter(needsReorder).length})
                                        </button>
                                        <button
                                            onClick={() => setStatusFilter('in-stock')}
                                            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                                                statusFilter === 'in-stock'
                                                    ? 'bg-green-600 text-white'
                                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                            }`}
                                        >
                                            In Stock ({baseFilteredData.filter(item => !needsReorder(item)).length})
                                        </button>
                                    </div>
                                </div>

                            </div>
                        </section>

                        {/* Table Section */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col flex-1 min-h-0">
                            <div className="flex items-center justify-between mb-4 flex-shrink-0">
                                <div>
                                    <p className="text-sm text-slate-600">
                                        Showing <span className="font-semibold text-black">{filteredData.length}</span> items
                                        {searchQuery && ` (filtered from ${baseFilteredData.length} total)`}
                                    </p>
                                </div>
                            </div>
                            
                            <div className="flex-1 overflow-auto min-h-0 rounded-xl border border-slate-200">
                                <table className="min-w-full divide-y divide-slate-200 text-sm">
                                    <thead className="bg-gray-200 sticky top-0 z-10">
                                        <tr>
                                            <SortableHeader columnKey="urgency" label="Priority" className="w-20" />
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 w-16">S.No</th>
                                            <SortableHeader columnKey="itemName" label="Item Name" />
                                            <SortableHeader columnKey="company" label="Company" />
                                            <SortableHeader columnKey="onhand" label="On Hand" className="text-right" />
                                            <SortableHeader columnKey="reorderLevel" label="Reorder Level" className="text-right" />
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Stock Level</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Status</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 w-24">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-slate-100">
                                        {filteredData.length > 0 ? (
                                            filteredData.map((item, index) => {
                                                const isExpanded = expandedItems.has(item.Item_No);
                                                const history = purchaseHistories[item.Item_No] || [];
                                                const mostAffordable = getMostAffordablePurchase(history);
                                                const needsReorderFlag = needsReorder(item);
                                                const stockPercentage = getStockPercentage(item);
                                                const onhand = parseFloat(item.Onhand || 0);
                                                const reorderLevel = getReorderDisplayValue(item);
                                                
                                                // Determine urgency color
                                                let urgencyColor = 'bg-green-100 text-green-800';
                                                let urgencyIcon = <FiCheckCircle className="text-green-600" size={16} />;
                                                if (onhand === 0) {
                                                    urgencyColor = 'bg-red-100 text-red-800';
                                                    urgencyIcon = <FiAlertCircle className="text-red-600" size={16} />;
                                                } else if (needsReorderFlag) {
                                                    urgencyColor = 'bg-red-100 text-red-800';
                                                    urgencyIcon = <FiAlertCircle className="text-red-600" size={16} />;
                                                } else if (stockPercentage < 50) {
                                                    urgencyColor = 'bg-amber-100 text-amber-800';
                                                    urgencyIcon = <FiTrendingDown className="text-amber-600" size={16} />;
                                                }
                                                
                                                return (
                                                    <React.Fragment key={`${item.Item_No}-${index}`}>
                                                        <tr className={`hover:bg-slate-50 transition ${needsReorderFlag ? 'bg-red-50/30' : ''}`}>
                                                            <td className="px-4 py-3">
                                                                <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${urgencyColor}`}>
                                                                    {urgencyIcon}
                                                                    {onhand === 0 ? 'Out' : needsReorderFlag ? 'Low' : stockPercentage < 50 ? 'Medium' : 'Good'}
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 text-slate-600 font-medium">{index + 1}</td>
                                                            <td className="px-4 py-3">
                                                                <div className="font-medium text-slate-900">{item.Item_Name || '-'}</div>
                                                            </td>
                                                            <td className="px-4 py-3 text-slate-600">{item.Company || '-'}</td>
                                                            <td className="px-4 py-3 text-left">
                                                                <span className="font-semibold text-slate-800">{onhand.toLocaleString()}</span>
                                                            </td>
                                                            <td className="px-4 py-3 text-left">
                                                                <span className="font-semibold text-slate-700">
                                                                    {Number.isInteger(reorderLevel)
                                                                        ? reorderLevel.toLocaleString()
                                                                        : reorderLevel.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="flex items-center gap-2">
                                                                    <div className="flex-1 bg-slate-200 rounded-full h-2 overflow-hidden">
                                                                        <div 
                                                                            className={`h-full transition-all ${
                                                                                stockPercentage === 0 ? 'bg-red-500' :
                                                                                stockPercentage < 50 ? 'bg-amber-500' :
                                                                                stockPercentage < 100 ? 'bg-yellow-500' :
                                                                                'bg-green-500'
                                                                            }`}
                                                                            style={{ width: `${Math.min(100, stockPercentage)}%` }}
                                                                        />
                                                                    </div>
                                                                    <span className="text-xs font-medium text-slate-600 w-12 text-right">
                                                                        {stockPercentage.toFixed(0)}%
                                                                    </span>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                                                    needsReorderFlag 
                                                                        ? 'bg-red-100 text-red-800' 
                                                                        : 'bg-green-100 text-green-800'
                                                                }`}>
                                                                    {needsReorderFlag ? 'Needs Reorder' : 'In Stock'}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <button
                                                                    onClick={() => toggleItemExpansion(item.Item_No)}
                                                                    className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition"
                                                                >
                                                                    {isExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                                                    {isExpanded ? 'Hide' : 'View'}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                        {isExpanded && (
                                                            <tr>
                                                                <td colSpan={10} className="px-4 py-4 bg-slate-50/50">
                                                                    <div className="space-y-4">
                                                                        <div className="flex items-center justify-between">
                                                                            <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                                                                                <span>Purchase History</span>
                                                                                {mostAffordable && (
                                                                                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                                                                        <FiTrendingDown size={12} />
                                                                                        Best Price: ₹{parseFloat(mostAffordable.Price || 0).toFixed(2)}
                                                                                    </span>
                                                                                )}
                                                                            </h4>
                                                                            {loadingHistories.has(item.Item_No) && (
                                                                                <span className="text-xs text-slate-500">Loading...</span>
                                                                            )}
                                                                        </div>
                                                                        {history.length > 0 ? (
                                                                            <div className="overflow-x-auto rounded-lg border border-slate-200">
                                                                                <table className="min-w-full divide-y divide-slate-200 text-xs">
                                                                                    <thead className="bg-slate-100">
                                                                                        <tr>
                                                                                            <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Supplier</th>
                                                                                            <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700">Packing</th>
                                                                                            <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700">FOC</th>
                                                                                            <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700">Purchase Price</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody className="bg-white divide-y divide-slate-200">
                                                                                        {history.map((purchase, idx) => {
                                                                                            const purchasePrice = parseFloat(purchase.Price || 0);
                                                                                            const isMostAffordable = mostAffordable &&
                                                                                                purchasePrice === parseFloat(mostAffordable.Price || 0);
                                                                                            
                                                                                            return (
                                                                                                <tr 
                                                                                                    key={`${purchase.SupplierName}-${idx}`}
                                                                                                    className={isMostAffordable ? 'bg-green-50 border-l-4 border-green-500' : 'hover:bg-slate-50'}
                                                                                                >
                                                                                                    <td className="px-3 py-2 text-slate-800 font-medium">{purchase.SupplierName}</td>
                                                                                                    <td className="px-3 py-2 text-right text-slate-800">{purchase.packing ?? '-'}</td>
                                                                                                    <td className="px-3 py-2 text-right text-slate-800">{parseFloat(purchase.foc || 0).toLocaleString()}</td>
                                                                                                    <td className={`px-3 py-2 text-right font-semibold ${isMostAffordable ? 'text-green-700' : 'text-slate-800'}`}>
                                                                                                        ₹{purchasePrice.toFixed(2)}
                                                                                                        {isMostAffordable && <span className="ml-2 text-xs text-green-600 font-normal">(Best)</span>}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            );
                                                                                        })}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        ) : (
                                                                            <p className="text-sm text-slate-500 py-4 text-center bg-slate-50 rounded-lg">
                                                                                No purchase history available for this item.
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={10} className="px-4 py-12 text-center">
                                                    <div className="flex flex-col items-center gap-2">
                                                        <FiSearch className="text-slate-400" size={32} />
                                                        <p className="text-slate-500 font-medium">No items found</p>
                                                        <p className="text-xs text-slate-400">
                                                            {searchQuery ? 'Try adjusting your search or filters' : 'No items with reorder level set'}
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default withAuth(ReorderLevel);

